<template>
  <v-dialog :value="value" @input="onDialogInput" max-width="400" persistent>
    <modal-card title="Schedule Callback" padding @close="close">
      <v-menu
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template #activator="{ on }">
          <v-text-field
            v-on="on"
            :value="formattedDate"
            label="Date"
            append-icon="event"
            readonly
            class="background-dark mb-5 mt-3"
          />
        </template>
        <v-date-picker
          v-model="selectedDate"
          :min="minDate"
          color="primary"
          no-title
          @input="dateMenu = false"
        />
      </v-menu>

      <v-menu
        v-model="timeMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="290px"
      >
        <template #activator="{ on }">
          <v-text-field
            v-on="on"
            :value="formattedTime"
            label="Time"
            append-icon="access_time"
            class="background-dark mb-3"
            dense
            readonly
          />
        </template>
        <v-time-picker
          v-if="timeMenu"
          v-model="selectedTime"
          color="primary"
          ampm-in-title
          :allowed-minutes="allowedMinutes"
          @click:minute="timeMenu = false"
        />
      </v-menu>

      <template #actions>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="appMain"
          :disabled="!selectedDate || !selectedTime"
          @click="saveToCalendar"
        >
          Save
        </v-btn>
      </template>
    </modal-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { addHours, roundToNearestMinutes, format } from 'date-fns';
import { openCalendar } from '../calendarUtils';

const CALLBACK_DURATION_MS = 30 * 60 * 1000;

export default {
  name: 'ScheduleCallbackDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      dateMenu: false,
      timeMenu: false,
      selectedDate: '',
      selectedTime: '',
    };
  },
  computed: {
    ...mapGetters(['singleContact', 'activeContact']),

    contact() {
      return (
        this.singleContact(this.contactId) || this.activeContact(this.contactId)
      );
    },

    contactName() {
      return this.getContactName(this.contact);
    },

    minDate() {
      return new Date().toISOString().split('T')[0];
    },

    formattedDate() {
      if (!this.selectedDate) return '';
      const [year, month, day] = this.selectedDate.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    formattedTime() {
      if (!this.selectedTime) return '';
      const [hours, minutes] = this.selectedTime.split(':').map(Number);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const h = hours % 12 || 12;
      return `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
    },

    contactProfileUrl() {
      if (!this.contactId) return '';
      const { href } = this.$router.resolve({
        name: 'contact-edit',
        params: { id: this.contactId },
      });
      return new URL(href, window.location.href).toString();
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.initDefaults();
    },
  },
  methods: {
    allowedMinutes(v) {
      return v % 15 === 0;
    },

    initDefaults() {
      const date = roundToNearestMinutes(addHours(new Date(), 1), {
        nearestTo: 15,
        roundingMethod: 'ceil',
      });
      this.selectedDate = format(date, 'yyyy-MM-dd');
      this.selectedTime = format(date, 'HH:mm');
    },

    resetState() {
      this.dateMenu = false;
      this.timeMenu = false;
      this.selectedDate = '';
      this.selectedTime = '';
    },

    close() {
      this.$emit('input', false);
      this.resetState();
    },

    onDialogInput(isOpen) {
      if (!isOpen) this.close();
    },

    saveToCalendar() {
      const [year, month, day] = this.selectedDate.split('-').map(Number);
      const [hours, minutes] = this.selectedTime.split(':').map(Number);
      const start = new Date(year, month - 1, day, hours, minutes);
      const end = new Date(start.getTime() + CALLBACK_DURATION_MS);

      const url = this.contactProfileUrl;
      const descParts = [];
      if (url) {
        descParts.push(`Contact profile: ${url}`);
      }
      if (this.contact?.mobile) {
        descParts.push(`Mobile: ${this.contact.mobile}`);
      }

      const event = {
        uid: `callback-${this.contactId}-${Date.now()}@conveyour.com`,
        start: start.toISOString(),
        end: end.toISOString(),
        summary: `Callback - ${this.contactName}`,
        ...(descParts.length ? { description: descParts.join('\n') } : {}),
        ...(url ? { url } : {}),
      };

      openCalendar(event);

      setTimeout(() => {
        this.$emit('saved', { contactId: this.contactId });
        this.close();
      }, 1000);
    },
  },
};
</script>
